import isEmpty from "lodash/isEmpty"
import { EmptyObj } from "types-lib/objects"
import isHomePagePath from "utils-lib/isHomePagePath"

const LOCALE_REGEX = /^\/(us|ca)\/(en|fr)/
const LOCATION_REGEX = /^\/(us|ca)\/(en|fr)\/location\/.+/
const LOCATION_L1_ROUTE = "/location"

const getIsLocalPageRouteEnabledOrDisabled = (
  pathname: string,
  isRouteEnabled: boolean,
  disabledRoutes: Record<string, boolean>,
): boolean => {
  const isL1RouteDisabled = disabledRoutes[LOCATION_L1_ROUTE]
  const isRouteDisabled = disabledRoutes[pathname]

  // Check if the L1 route is disabled, the route is enabled, and the specific route is not disabled
  if (isL1RouteDisabled && isRouteEnabled && !isRouteDisabled) {
    return true
  }

  // Check if the specific route is disabled but the route is enabled
  if (isRouteDisabled && isRouteEnabled) {
    return false
  }

  // Default to false if none of the above conditions are met
  return false
}

/**
 * Checks if a given route is enabled or disabled based on the provided routes configuration.
 * Either enabledRoutes or disabledRoutes objects can be passed into the routes param.
 * Specifically we are looking for a match, we'll leave the business logic to the caller.
 * Both enabledRoutes and disabledRoutes objects share a similar structure.
 *
 * Example enabledRoutes object:
 * {
 *   "/us/en": true, // homepage is enabled for /us/en
 *   "/privacy": true, // /.+/privacy/.+/ is enabled
 *   "/recycle-right": true, // all L1 recycle-right pages are enabled, matches /.+/recycle-right/.+/
 *   // specific L2 recycle-right page is enabled, this will override the L1 configuration above
 *   "/us/en/recycle-right/recycling-101": true,
 *   "/ca/fr/privacy": true,
 *   // any other L2, L3, etc pages will default to disabled, unless explicitly enabled either at the L1 level or a specific page path
 * }
 *
 * Example disabledRoutes object:
 * {
 *   "/us/en/recycle-right/recycling-101": true, // specific L2 page is disabled
 * }
 *
 * @param {Object.<string, boolean>} routes - The routes configuration object.
 * @param {string} pathname - The pathname to check.
 * @returns {boolean} - Returns true if a match is found for the pathname in the routes object..
 */
const getIsRouteEnabledOrDisabled = (
  routes: {
    [x: string]: boolean
  },
  pathname: string,
): boolean => {
  const routeSegments = pathname.split("/").filter(Boolean)

  let locale = ""
  let route = ""
  let l1RouteWithLocale = ""

  // check if the locale is present in the pathname at the start, this is all we need to do if the route is a root route
  const localeSegment = `/${routeSegments[0]}/${routeSegments[1]}`
  if (LOCALE_REGEX.test(localeSegment)) {
    locale = localeSegment
  }

  // this is a L1, L2, L3, etc route, we only need to check the L1 "root" route
  if (routeSegments?.length > 2) {
    route = `/${routeSegments[2]}`

    if (locale) {
      l1RouteWithLocale = `${locale}/${routeSegments[2]}`
    }
  }

  const isRootOrL1Matched: boolean =
    routes[locale] || routes[route] || routes[l1RouteWithLocale]

  // default to false, this saves us from having to add every possible route to the routes
  let isMatched = false

  // check if the rootRoute (/country/lang) or L1 route, with or without locale (/country/lang/recycle-right OR /recyle-right) is matched
  if (isRootOrL1Matched) {
    // do nothing
    isMatched = true
  }

  const l2OrL3PathWithoutLocale = pathname.replace(LOCALE_REGEX, "")

  // check if the full path L2, L3, etc is matched (/country/lang/recyle-right/recyling-101), this will override the above check
  // only run this check if the route is explicitly defined in the routes object
  // this will also match the route if the locale is not present in the pathname
  // we are not enabling child routes of L2, L3, etc pages by default, they need to be explicitly enabled
  if (
    Object.keys(routes).includes(pathname) ||
    Object.keys(routes).includes(l2OrL3PathWithoutLocale)
  ) {
    const isL2OrL3Matched: boolean =
      routes[pathname] || routes[l2OrL3PathWithoutLocale]

    if (isL2OrL3Matched) {
      isMatched = true
    }
  }

  // isMatched will be true if the path is matched in the routes object
  // the routes object can either be enabledRoutes or disabledRoutes
  // if the path is not found in the routes object, isMatched will default to false
  return isMatched
}

type RouteFlags = {
  isHomePageDisabled?: boolean
  enabledByDefault: boolean
  enabledRoutes: Record<string, boolean>
  disabledRoutes: Record<string, boolean>
}

/**
 * Determines if a given route is enabled for Next.js.
 *
 * @param {string} pathname - The path of the route to check.
 * @param {boolean} enabledByDefault - A flag indicating if routes are enabled by default.
 * @param {Record<string, boolean>} enabledRoutes - An object where keys are routes that are explicitly enabled.
 * @param {Record<string, boolean>} disabledRoutes - An object where keys are routes that are explicitly disabled.
 * @returns {boolean} - Returns true if the route is enabled or enabled by default and not disabled, otherwise false.
 */
const getIsRouteNextJsEnabled = (
  pathname: string,
  {
    enabledByDefault,
    enabledRoutes,
    disabledRoutes,
    isHomePageDisabled,
  }: RouteFlags | EmptyObj = {
    enabledByDefault: false,
    enabledRoutes: {},
    disabledRoutes: {},
  },
): boolean => {
  // pathname is required, if it is not a string, return false
  if (typeof pathname !== "string") return false

  // only match homepage path on a direct match, not an L1 match
  // return false if the homepage is disabled and the pathname matches a homepage path
  if (isHomePageDisabled && isHomePagePath(pathname)) {
    return false
  }

  let isNextJsEnabled = false

  const isRouteEnabled = !isEmpty(enabledRoutes)
    ? getIsRouteEnabledOrDisabled(enabledRoutes, pathname)
    : false
  const isRouteDisabled = !isEmpty(disabledRoutes)
    ? getIsRouteEnabledOrDisabled(disabledRoutes, pathname)
    : false

  if (isRouteEnabled || enabledByDefault) {
    isNextJsEnabled = true
  }

  if (isRouteDisabled) {
    /*
     * For local pages, allow enabledRoutes to override disabledRoutes
     * if disabledRoutes contains a match for `/location`.
     * this allows us to enable all other pages by default and only enable select local pages
     * this is to prevent the need to add every possible route to the routes object, since there are thousands of them
     * only check when isRouteDisabled is true
     */
    if (LOCATION_REGEX.test(pathname)) {
      isNextJsEnabled = getIsLocalPageRouteEnabledOrDisabled(
        pathname,
        isRouteEnabled,
        disabledRoutes,
      )
    } else {
      isNextJsEnabled = false
    }
  }

  return isNextJsEnabled
}

export default getIsRouteNextJsEnabled
